import NumberFlow from '@number-flow/react'
import * as Slider from '@radix-ui/react-slider'
import { useId, useState } from 'react'

const MIN = 20
const MAX = 120
const STEP = 5

const clamp = (speed: number) => Math.min(MAX, Math.max(MIN, Math.round(speed)))

export default function BardBrakingDistanceCalculator() {
  const id = useId()
  const [speed, setSpeed] = useState(50)
  const [input, setInput] = useState('50')

  const updateSpeed = (value: number) => {
    setSpeed(value)
    setInput(String(value))
  }

  // Faustformeln aus dem Artikel, 1 Sekunde Reaktionszeit
  const tenth = speed / 10
  const reaction = tenth * 3
  const braking = tenth * tenth
  const emergencyBraking = braking / 2

  const results = [
    { label: 'Reaktionsweg', value: reaction },
    { label: 'Bremsweg normal', value: braking },
    { label: 'Bremsweg Gefahrenbremsung', value: emergencyBraking },
    { label: 'Anhalteweg normal', value: reaction + braking },
    {
      label: 'Anhalteweg Gefahrenbremsung',
      value: reaction + emergencyBraking,
    },
  ]

  return (
    <div className="rounded-lg bg-purple-100 px-4 py-6 md:px-10 md:py-8">
      <label htmlFor={id} className="block text-lg font-bold">
        Geschwindigkeit: {speed} km/h
      </label>
      <div className="mt-3 flex items-center gap-4">
        <Slider.Root
          value={[speed]}
          min={MIN}
          max={MAX}
          step={STEP}
          onValueChange={([value]) => updateSpeed(value)}
          className="relative flex h-6 flex-1 cursor-pointer touch-none items-center select-none"
        >
          <Slider.Track className="relative h-2 w-full rounded-full bg-white">
            <Slider.Range className="absolute h-full rounded-full bg-purple-600" />
          </Slider.Track>
          <Slider.Thumb
            aria-label="Geschwindigkeit in km/h"
            className="block size-6 rounded-full border-2 border-white bg-purple-600 shadow-sm transition-shadow focus:ring-2 focus:ring-purple-600 focus:outline-none"
          />
        </Slider.Root>
        <input
          id={id}
          type="number"
          inputMode="numeric"
          min={MIN}
          max={MAX}
          step={STEP}
          value={input}
          onChange={(event) => {
            setInput(event.target.value)
            const value = Number(event.target.value)
            if (value >= MIN && value <= MAX) setSpeed(Math.round(value))
          }}
          onBlur={() => {
            const value = Number(input)
            updateSpeed(input && Number.isFinite(value) ? clamp(value) : speed)
          }}
          className="w-20 rounded-md border border-gray-300 bg-white px-3 py-2 font-bold outline-none focus:border-purple-600 focus:ring-1 focus:ring-purple-600"
        />
      </div>

      <p className="mt-6 text-xl font-bold">Ergebnis bei {speed} km/h</p>
      <dl className="mt-3 grid gap-3 sm:grid-cols-2">
        {results.map((result) => (
          <div
            key={result.label}
            className="rounded-lg bg-white px-5 py-4 sm:first:col-span-2"
          >
            <dt>{result.label}</dt>
            <dd className="mt-1 text-2xl font-black">
              <NumberFlow
                value={result.value}
                locales="de-CH"
                format={{ maximumFractionDigits: 1 }}
                suffix=" m"
              />
            </dd>
          </div>
        ))}
      </dl>
      <p className="mt-4 text-sm text-gray-800">
        Der Rechner verwendet einfache Faustformeln und geht von einer
        Reaktionszeit von einer Sekunde aus. Die tatsächlichen Werte können
        davon abweichen.
      </p>
    </div>
  )
}
